// The libxml entity behavior shared by the text and HTML save pipelines.
//
// fixtures/html_entities.json is generated from the LIVE stack by
// scripts/parity/gen_text_entities.php — it records, per character, what
// survives a saveHTML() + html_entity_decode(ENT_QUOTES|ENT_XML1) round trip
// (so `é` lands as `&eacute;` and U+00A0 as `&nbsp;`, while & < > " ' come back
// as raw characters), and per named entity what a parse + that round trip
// yields.

import entityTablesJson from '../fixtures/html_entities.json'

interface EntityTables {
  /** raw char -> literal entity text that survives in the output */
  encode: Record<string, string>
  /** entity name -> full-roundtrip result */
  decode: Record<string, string>
}

let tables: EntityTables | null = null
let plainDecode: Record<string, string> | null = null

function loadTables(): EntityTables {
  if (tables) return tables
  tables = entityTablesJson as EntityTables
  const encodeInverse: Record<string, string> = {}
  for (const [ch, ent] of Object.entries(tables.encode)) encodeInverse[ent] = ch
  // The character each named entity represents (used by the parse passes).
  plainDecode = {}
  for (const [name, roundtrip] of Object.entries(tables.decode)) {
    plainDecode[name] = encodeInverse[roundtrip] ?? roundtrip
  }
  return tables
}

/** The libxml named-entity table (name -> character). */
export function namedEntities(): Record<string, string> {
  loadTables()
  return plainDecode!
}

/** Parser-side entity decoding (numeric always; named per the libxml table). */
export function decodeParserEntities(text: string, table: Record<string, string> = namedEntities()): string {
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g, (whole, body: string) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X'
      const cp = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10)
      if (!Number.isFinite(cp) || cp <= 0 || cp > 0x10ffff) return whole
      try {
        return String.fromCodePoint(cp)
      } catch {
        return whole
      }
    }
    return body in table ? table[body] : whole
  })
}

/** saveHTML encoding + the ENT_XML1 decode — the entities that survive. */
export function encodeSurvivingEntities(text: string): string {
  const { encode } = loadTables()
  let out = ''
  for (const ch of text) {
    out += encode[ch] ?? ch
  }
  return out
}
